"use client";

import { clearPersistedKeycloakTokens, getKeycloak, KC_TOKENS_STORAGE_KEY } from "@/lib/keycloak";
import { hasPdnCookieConsent } from "@/lib/pdnCookieConsent";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";

type Props = {
  children: React.ReactNode;
};

type PersistedTokens = {
  token?: string;
  refreshToken?: string;
  idToken?: string;
};

const REFRESH_INTERVAL_MS = 60_000;
const REFRESH_MIN_VALIDITY_SECONDS = 90;
const MAX_REFRESH_FAILURES = 6;
const RETRY_DELAYS_MS = [1_000, 3_000, 10_000, 30_000];
const SESSION_TIMEOUT_MS = 10 * 60 * 60 * 1000;

function loadPersistedTokens(): PersistedTokens {
  try {
    const raw = window.localStorage.getItem(KC_TOKENS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PersistedTokens;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function savePersistedTokens(tokens: PersistedTokens) {
  try {
    window.localStorage.setItem(KC_TOKENS_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // Ignore localStorage write failures (private mode, quota, etc).
  }
}

function clearPersistedTokens() {
  clearPersistedKeycloakTokens();
}

function isNavigatorOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  if (typeof navigator.onLine !== "boolean") return true;
  return navigator.onLine;
}

function getSessionAuthTimestampMs(kc: any): number | null {
  const parsed =
    kc?.tokenParsed && typeof kc.tokenParsed === "object"
      ? kc.tokenParsed
      : kc?.idTokenParsed && typeof kc.idTokenParsed === "object"
        ? kc.idTokenParsed
        : null;
  const authTimeSeconds =
    typeof parsed?.auth_time === "number"
      ? parsed.auth_time
      : typeof parsed?.iat === "number"
        ? parsed.iat
        : null;
  return authTimeSeconds ? authTimeSeconds * 1000 : null;
}

function syncKeycloakTokens(kc: {
  token?: string;
  refreshToken?: string;
  idToken?: string;
}) {
  (window as any).__hubcrmAccessToken = kc.token;
  savePersistedTokens({
    token: kc.token,
    refreshToken: kc.refreshToken,
    idToken: kc.idToken,
  });
}

export default function KeycloakGate({ children }: Props) {
  const router = useRouter();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;
    let intervalId: number | null = null;
    let retryTimeoutId: number | null = null;
    let sessionTimeoutId: number | null = null;
    let handleOnline: (() => void) | null = null;

    (async () => {
      try {
        const kc = await getKeycloak();
        const persisted = loadPersistedTokens();
        const authenticated = await kc.init({
          pkceMethod: "S256",
          checkLoginIframe: false,
          token: persisted.token,
          refreshToken: persisted.refreshToken,
          idToken: persisted.idToken,
        });

        if (!authenticated) {
          clearPersistedTokens();
          router.replace("/signin");
          return;
        }

        if (!hasPdnCookieConsent()) {
          clearPersistedTokens();
          await kc.logout({
            redirectUri: `${window.location.origin}/signin`,
          });
          return;
        }

        let refreshFailures = 0;
        let refreshInFlight = false;
        let authRedirectStarted = false;

        const clearTimers = () => {
          if (intervalId !== null) window.clearInterval(intervalId);
          if (retryTimeoutId !== null) window.clearTimeout(retryTimeoutId);
          if (sessionTimeoutId !== null) window.clearTimeout(sessionTimeoutId);
        };

        const logoutDueToSessionTimeout = async () => {
          if (disposed || authRedirectStarted) return;
          authRedirectStarted = true;
          clearTimers();
          clearPersistedTokens();
          await kc.logout({
            redirectUri: `${window.location.origin}/signin`,
          });
        };

        const armSessionTimeout = () => {
          if (sessionTimeoutId !== null) {
            window.clearTimeout(sessionTimeoutId);
            sessionTimeoutId = null;
          }
          const authTimestampMs = getSessionAuthTimestampMs(kc);
          if (!authTimestampMs) return;
          const remainingMs = authTimestampMs + SESSION_TIMEOUT_MS - Date.now();
          if (remainingMs <= 0) {
            void logoutDueToSessionTimeout();
            return;
          }
          sessionTimeoutId = window.setTimeout(() => {
            sessionTimeoutId = null;
            void logoutDueToSessionTimeout();
          }, remainingMs);
        };

        const loginIfSessionIsLost = async () => {
          if (disposed || authRedirectStarted) return;
          authRedirectStarted = true;
          clearTimers();
          clearPersistedTokens();
          router.replace("/signin");
        };

        const scheduleRetry = () => {
          if (disposed || retryTimeoutId !== null) return;
          const delay =
            RETRY_DELAYS_MS[Math.min(refreshFailures - 1, RETRY_DELAYS_MS.length - 1)];
          retryTimeoutId = window.setTimeout(() => {
            retryTimeoutId = null;
            void tryRefreshToken(true);
          }, delay);
        };

        const tryRefreshToken = async (forced = false) => {
          if (disposed || refreshInFlight || authRedirectStarted) return;
          if (!forced && !isNavigatorOnline()) return;
          refreshInFlight = true;

          try {
            const refreshed = await kc.updateToken(REFRESH_MIN_VALIDITY_SECONDS);
            refreshFailures = 0;
            if (refreshed || kc.token) {
              syncKeycloakTokens(kc);
              armSessionTimeout();
            }
          } catch {
            refreshFailures += 1;
            if (refreshFailures >= MAX_REFRESH_FAILURES) {
              await loginIfSessionIsLost();
              return;
            }
            scheduleRetry();
          } finally {
            refreshInFlight = false;
          }
        };

        handleOnline = () => {
          void tryRefreshToken(true);
        };

        syncKeycloakTokens(kc);
        armSessionTimeout();
        intervalId = window.setInterval(() => {
          void tryRefreshToken();
        }, REFRESH_INTERVAL_MS);
        window.addEventListener("online", handleOnline);

        if (disposed) return;
        setReady(true);
      } catch (e: any) {
        if (disposed) return;
        setError(e?.message || "Keycloak init failed");
        // fallback: go to template signin page
        router.replace("/signin");
      }
    })();

    return () => {
      disposed = true;
      if (intervalId !== null) window.clearInterval(intervalId);
      if (retryTimeoutId !== null) window.clearTimeout(retryTimeoutId);
      if (sessionTimeoutId !== null) window.clearTimeout(sessionTimeoutId);
      if (handleOnline) window.removeEventListener("online", handleOnline);
    };
  }, [router]);

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-600 font-medium">Auth error</div>
        <div className="text-sm text-gray-600 mt-2">{error}</div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="p-6">
        <div className="text-sm text-gray-600">Signing you in...</div>
      </div>
    );
  }

  return <>{children}</>;
}


